export const RECEIPT_HIDE_TAX_KEY = 'receipt_hide_tax';
export const RECEIPT_HIDE_DISCOUNT_KEY = 'receipt_hide_discount';
export const RECEIPT_HIDE_CASHIER_KEY = 'receipt_hide_cashier';
export const RECEIPT_HIDE_PAYMENT_METHOD_KEY = 'receipt_hide_payment_method';

/**
 * The muted second line under a product's name — family, SKU, material,
 * colour, size, type, dimensions, in that order — and which of the seven a
 * surface shows. The sale invoice reads `sale_receipt_line_*` (all off, so the
 * invoice prints as it always has) and the POS cart `pos_line_*` (the family
 * on, the rest off). One reader and one formatter for both, so the till and
 * the paper cannot disagree about what a switch means or how a figure prints.
 */
export const LINE_DETAIL_FIELDS = ['family', 'sku', 'material', 'color', 'size', 'type', 'dimensions'];
export const SALE_RECEIPT_LINE_DETAIL_PREFIX = 'sale_receipt_line_';
export const POS_LINE_DETAIL_PREFIX = 'pos_line_';
export const POS_LINE_DETAIL_DEFAULTS = { family: true };

export function lineDetailSwitches(config = {}, prefix = SALE_RECEIPT_LINE_DETAIL_PREFIX, defaults = {}) {
    const switches = {};
    for (const field of LINE_DETAIL_FIELDS) {
        switches[field] = configFlagOn(config?.[prefix + field], Boolean(defaults[field]));
    }
    return switches;
}

/**
 * "AAA x BBB x CCC" when all three dimensions are set, "AAA x BBB" for any
 * two (length, width, height order), "L:AAA" / "W:AAA" / "H:AAA" for one, and
 * '' for none. A dimension is set when it is a number above zero; figures
 * print as saved, without trailing zeros (12.50 -> 12.5).
 */
export function lineDimensionsLabel(line = {}) {
    const set = [
        ['L', line?.length],
        ['W', line?.width],
        ['H', line?.height],
    ]
        .map(([axis, value]) => [axis, Number(value)])
        .filter(([, value]) => Number.isFinite(value) && value > 0)
        .map(([axis, value]) => [axis, String(value)]);
    if (set.length === 0) return '';
    if (set.length === 1) return `${set[0][0]}:${set[0][1]}`;
    return set.map(([, value]) => value).join(' x ');
}

/**
 * The details to print under one line, in the order the switches are laid
 * out, each only when its switch is on and the line carries it. Raw text —
 * the caller escapes. The dimensions ride in brackets as one part.
 */
export function lineDetailParts(line = {}, switches = {}) {
    const text = (value) => String(value ?? '').trim();
    const parts = [];
    if (switches.family && text(line?.family_name)) parts.push(text(line.family_name));
    if (switches.sku && text(line?.sku)) parts.push(text(line.sku));
    if (switches.material && text(line?.material)) parts.push(text(line.material));
    if (switches.color && text(line?.color)) parts.push(text(line.color));
    if (switches.size && text(line?.size)) parts.push(text(line.size));
    if (switches.type && text(line?.type)) parts.push(text(line.type));
    if (switches.dimensions) {
        const dimensions = lineDimensionsLabel(line);
        if (dimensions) parts.push(`(${dimensions})`);
    }
    return parts;
}

/** The parts joined with middle dots, or '' when there is nothing to print. */
export function lineDetailText(line = {}, switches = {}) {
    return lineDetailParts(line, switches).join(' · ');
}

/** Global receipt setting: hide the Tax column on line-item tables only. */
export function receiptHideTax(config = {}) {
    return configFlagOn(config[RECEIPT_HIDE_TAX_KEY], false);
}

/** Global receipt setting: hide the Discount column on line-item tables only. */
export function receiptHideDiscount(config = {}) {
    return configFlagOn(config[RECEIPT_HIDE_DISCOUNT_KEY], false);
}

export function showReceiptLineItemTax(config = {}) {
    return !receiptHideTax(config);
}

export function showReceiptLineItemDiscount(config = {}) {
    return !receiptHideDiscount(config);
}

/**
 * Global receipt setting: hide who served the document — the sale invoice's
 * Cashier line, the exchange slip's Processed Via line and the supplier
 * receipt's Served by line.
 */
export function receiptHideCashier(config = {}) {
    return configFlagOn(config[RECEIPT_HIDE_CASHIER_KEY], false);
}

/**
 * Global receipt setting: hide how the document was paid — the payment method
 * line and the transaction reference that rides on it. The amounts settled to
 * each destination are money lines and stay.
 */
export function receiptHidePaymentMethod(config = {}) {
    return configFlagOn(config[RECEIPT_HIDE_PAYMENT_METHOD_KEY], false);
}

export function showReceiptCashier(config = {}) {
    return !receiptHideCashier(config);
}

export function showReceiptPaymentMethod(config = {}) {
    return !receiptHidePaymentMethod(config);
}

/** Summary rows omit zero or non-applicable tax/discount amounts. */
export function receiptSummaryAmountVisible(value) {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) && amount > 0;
}

/** Check if a non-table value is meaningful (non-zero, non-blank, not placeholder). */
export function isReceiptMeaningfulValue(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
    const str = String(value).trim();
    if (!str) return false;
    const lower = str.toLowerCase();
    if (['-', '—', '–', 'n/a', 'na', 'null', 'undefined', 'none'].includes(lower)) {
        return false;
    }
    const stripped = str
        .replace(/^(?:rs\.?|pkr|[-+])\s*/i, '')
        .replace(/\s*(?:due|qty|items?|orders?)$/i, '')
        .trim();
    if (stripped !== '' && !Number.isNaN(Number(stripped)) && Number(stripped) === 0) {
        return false;
    }
    return true;
}

export function receiptThermalItemDetailColumnCount(config = {}) {
    let count = 3;
    if (showReceiptLineItemTax(config)) count += 1;
    if (showReceiptLineItemDiscount(config)) count += 1;
    return count;
}

/**
 * The customer's account position on the sale receipt, printed with their
 * details (under Invoice To on paper, in the Customer section on 80mm)
 * rather than with the totals: one row for what they still owe across every
 * invoice and one for the store credit they hold. `outstanding` is
 * Customer::outstandingBalance() as the sale payload
 * carries it, this sale included; a negative wallet is the same debt the
 * receivable already counts, so only a positive wallet prints. Empty for a
 * walk-in sale and for a settled account — a zero row is never printed.
 */
export function receiptCustomerBalanceLines(customer = {}) {
    const outstanding = Number(customer?.outstanding ?? 0);
    const wallet = Number(customer?.wallet_balance ?? 0);
    const lines = [];
    if (receiptSummaryAmountVisible(outstanding)) lines.push({ label: 'Outstanding Balance', amount: outstanding });
    if (receiptSummaryAmountVisible(wallet)) lines.push({ label: 'Credit Balance', amount: wallet });
    return lines;
}

/**
 * The delivery block a sale receipt prints when the order was placed with
 * delivery details (`sale.delivery`, Sale::deliveryDetails on the server):
 * who receives it, where, and what the customer asked for. Label/value rows,
 * unescaped — the caller escapes. Empty for a counter sale.
 */
export function receiptDeliveryLines(delivery) {
    if (!delivery || typeof delivery !== 'object') return [];
    const join = (parts, separator) =>
        parts
            .map((part) => String(part ?? '').trim())
            .filter(Boolean)
            .join(separator);
    return [
        { label: 'Deliver to', value: join([delivery.name, delivery.phone], ' · ') },
        { label: 'Address', value: join([delivery.address, delivery.city], ', ') },
        { label: 'Instructions', value: join([delivery.instructions], '') },
    ].filter((line) => line.value !== '');
}

/**
 * A split checkout's payments as receipt rows (docs/pos-split-tender-plan.md):
 * the method, where it landed (the card machine or the account) and its
 * reference, then what it settled — and, for money in another currency, what
 * changed hands. `tenders` is `sale.tenders` from the sale payload. Unescaped;
 * the caller escapes. Empty unless there are two or more.
 */
export function receiptTenderLines(tenders, { currencyLabel = 'Rs', format = (value) => Number(value || 0).toFixed(2) } = {}) {
    if (!Array.isArray(tenders) || tenders.length < 2) return [];
    const title = (method) =>
        String(method || '')
            .replace(/[_-]+/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    return tenders.map((tender) => {
        const place = tender.card_machine || tender.account || '';
        const label = [title(tender.method), place, tender.transaction_id ? `Ref ${tender.transaction_id}` : ''].filter(Boolean).join(' · ');
        const foreign = tender.currency_code ? ` (${tender.currency_code} ${format(tender.currency_amount)})` : '';
        return { label, value: `${currencyLabel} ${format(tender.amount)}${foreign}` };
    });
}

export function applySharedReceiptDesign(html, receiptSize = '80mm') {
    if (String(html).includes('data-inventree-shared-receipt-design')) {
        return String(html);
    }
    const size = String(receiptSize || '80mm')
        .trim()
        .toUpperCase();
    const isThermal = !['A4', 'A5', 'A5P'].includes(size);
    const isA5 = size === 'A5' || size === 'A5P';
    const isA5Portrait = size === 'A5P';
    // A document that declares its own 80mm page height (the POS sale receipt
    // and the multi-sale slip estimate one so the whole roll fits one sheet)
    // keeps it. Forcing `auto` here drops that estimate and a multi-item slip
    // that outgrows the driver's default page then paginates, leaving the
    // flex-reordered footer alone at the top of the next sheet.
    const ownsThermalPageHeight = isThermal && /@page[^}]*?size\s*:\s*80mm\s+\d+(?:\.\d+)?mm/i.test(String(html));
    const styles = `<style data-inventree-shared-receipt-design>
* { box-sizing: border-box !important; }
body {
    font-family: Arial, Helvetica, sans-serif !important;
    color: #111827 !important;
    background: #fff !important;
    overflow-wrap: anywhere !important;
}
.company-name, .company, .invoice-word, .title, .document-title {
    color: #000 !important;
    font-weight: 600 !important;
    text-transform: none !important;
}
.logo, .logo-img {
    display: block !important;
    object-fit: contain !important;
}
.company-meta, .company-sub, .subtitle, .branch-detail {
    color: #111827 !important;
}
.hr, .sep, .sep-dark, .rule {
    border-color: #333 !important;
}
.summary-separator { display: none !important; }
table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse !important;
}
thead { display: table-header-group !important; }
thead th {
    background: #f3f4f6 !important;
    color: #111827 !important;
    border-top: 1px solid #333 !important;
    border-bottom: 1px solid #333 !important;
    font-weight: 800 !important;
    text-transform: uppercase !important;
}
.total-line, .grand-total, .grand-total-row {
    background: #f3f4f6 !important;
    color: #111827 !important;
    border-top: 1px solid #333 !important;
    border-bottom: 1px solid #333 !important;
    font-weight: 800 !important;
}
.footer, .footer-bar {
    color: #000 !important;
    white-space: pre-line !important;
    overflow-wrap: anywhere !important;
}
tr, .total-line, .grand-total, .grand-total-row, .footer, .footer-bar {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
}
${
    isThermal
        ? `
${ownsThermalPageHeight ? '' : '@page { size: 80mm auto !important; margin: 0 !important; }'}
html {
    width: 80mm !important;
    max-width: 80mm !important;
    margin: 0 auto !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    background: #fff !important;
}
body,
body.print-80mm,
body.sale-pos-receipt,
body.sale-document,
body.supplier-document,
body.purchase-order-document,
body.transfer-document,
body.gatepass-document,
body.refund-document,
body.exchange-document,
body.load-form-document,
body.order-request-document {
    width: 80mm !important;
    min-width: 80mm !important;
    max-width: 80mm !important;
    min-height: 0 !important;
    height: auto !important;
    margin: 0 auto !important;
    padding: 2mm 2.5mm !important;
    box-sizing: border-box !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    line-height: 1.25 !important;
    color: #000 !important;
    background: #fff !important;
    overflow-x: hidden !important;
    overflow-y: visible !important;
}
body.print-80mm > *,
body > *,
.document,
.page,
.half,
.receipt-container,
.print-preview {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
}
.header,
.brand,
.print-header {
    width: 100% !important;
    max-width: 100% !important;
    text-align: center !important;
    margin: 0 0 2mm !important;
    padding: 0 0 2mm !important;
    border-bottom: 1px dashed #000 !important;
    box-sizing: border-box !important;
}
.header-row {
    flex-direction: column !important;
    align-items: center !important;
    margin-bottom: 0 !important;
    width: 100% !important;
}
.header-left,
.header-right {
    width: 100% !important;
    max-width: 100% !important;
    text-align: center !important;
    padding: 0 !important;
}
.header .logo,
.brand .logo,
.header .logo-img,
.brand .logo-img,
.header-left .logo,
.header-left .logo-img,
.logo,
.logo-img,
.refund-logo {
    display: block !important;
    max-width: 60% !important;
    max-height: 22mm !important;
    margin: 0 auto 1.5mm !important;
    object-fit: contain !important;
}
.company,
.company-name {
    display: block !important;
    font-size: 16px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
    color: #000 !important;
    text-align: center !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
    margin-bottom: 1.5px !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.company-meta,
.company-sub,
.branch-detail,
.header-address,
.header-tax,
.print-details,
.print-details div {
    display: block !important;
    font-size: 10px !important;
    line-height: 1.25 !important;
    font-weight: 400 !important;
    color: #000 !important;
    text-align: center !important;
    margin-top: 1px !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.doc-title,
.sale-invoice-title,
.title,
.document-title,
.invoice-word,
.receipt-type {
    display: block !important;
    font-size: 13.5px !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
    color: #000 !important;
    text-align: center !important;
    text-transform: none !important;
    letter-spacing: 0.5px !important;
    margin: 1.5mm 0 !important;
}
.invoice-no,
.number {
    font-size: 9.5px !important;
    letter-spacing: 0.4px !important;
    text-align: center !important;
    white-space: normal !important;
    word-break: break-word !important;
}
.hr,
.sep,
.sep-dark,
.rule,
.header-rule {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    border: none !important;
    border-top: 1px dashed #000 !important;
    margin: 2mm 0 !important;
    padding: 0 !important;
}
.header + .hr,
.brand + .hr,
.print-header + .hr {
    display: none !important;
}
.section,
.section-title,
.info-label,
.pm-title,
.subtitle {
    display: block !important;
    width: 100% !important;
    font-size: 11px !important;
    line-height: 1.2 !important;
    font-weight: 400 !important;
    color: #000 !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
    margin: 2mm 0 1mm !important;
    padding: 0 !important;
    border: none !important;
    text-align: left !important;
}
.info-strip {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    gap: 0 !important;
    margin-bottom: 1mm !important;
}
.info-block {
    width: 100% !important;
    max-width: 100% !important;
    flex: none !important;
}
.line,
.info-row,
.pm-row,
.sum-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 1px 0 !important;
    font-size: 11px !important;
    line-height: 1.25 !important;
    color: #000 !important;
    gap: 4px !important;
    box-sizing: border-box !important;
}
.line > :first-child,
.info-row > :first-child,
.pm-row > :first-child,
.sum-row > :first-child,
.sum-row .sum-lbl,
.line span:first-child,
.info-row span:first-child,
.pm-row span:first-child,
.sum-row span:first-child {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.line > :last-child,
.info-row > :last-child,
.pm-row > :last-child,
.sum-row > :last-child,
.line span:last-child,
.info-row span:last-child,
.pm-row span:last-child,
.sum-row span:last-child {
    flex: 0 0 auto !important;
    min-width: 0 !important;
    max-width: 65% !important;
    text-align: right !important;
    white-space: nowrap !important;
    overflow-wrap: anywhere !important;
}
.line.bold,
.sum-row.bold,
.line.bold span,
.sum-row.bold span {
    font-weight: 400 !important;
}
.sale-meta-line {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    width: 100% !important;
}
.sale-meta-line > span:first-child {
    flex: 0 0 auto !important;
    min-width: 0 !important;
    text-align: left !important;
}
.sale-meta-line > span:last-child {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    text-align: right !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.order-number-line span:last-child,
.customer-line span:last-child,
.return-branch-row span:last-child,
.reason-row span:last-child {
    white-space: normal !important;
    word-break: break-word !important;
}
table,
.sale-items-table,
.items-table,
.supplier-items-table,
.po-items-table,
.transfer-items-table {
    display: table !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
    margin: 1.5mm 0 !important;
    border-spacing: 0 !important;
    box-sizing: border-box !important;
}
thead {
    display: table-header-group !important;
    width: 100% !important;
}
tbody {
    width: 100% !important;
}
tr {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    width: 100% !important;
    box-sizing: border-box !important;
}
thead th {
    background: #f3f4f6 !important;
    color: #000 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    font-size: 10.5px !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    padding: 2.5px 1px !important;
    vertical-align: middle !important;
    white-space: nowrap !important;
    word-break: normal !important;
    box-sizing: border-box !important;
}
tbody td {
    border-bottom: 1px dotted #bbb !important;
    font-size: 10.5px !important;
    line-height: 1.2 !important;
    color: #000 !important;
    padding: 2.5px 1px !important;
    vertical-align: middle !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    box-sizing: border-box !important;
}
tbody tr:last-child td {
    border-bottom: 1px solid #000 !important;
}

/* 5-Column Item Tables (POS/Sale, Refund/Exchange, PO, Order Request) */
 .sale-items-table th:nth-child(1), .sale-items-table td:nth-child(1),
.items-table th.col-rate, .items-table td.col-rate,
 .po-items-table th.col-rate, .po-items-table td.col-rate { width: 18% !important; text-align: right !important; }

 .sale-items-table th:nth-child(2), .sale-items-table td:nth-child(2),
.items-table th.col-qty, .items-table td.col-qty,
 .po-items-table th.col-qty, .po-items-table td.col-qty { width: 12% !important; text-align: center !important; }

 .sale-items-table th:nth-child(3), .sale-items-table td:nth-child(3),
.items-table th.col-tax, .items-table td.col-tax,
 .po-items-table th.col-tax, .po-items-table td.col-tax { width: 25% !important; text-align: center !important; }

 .sale-items-table th:nth-child(4), .sale-items-table td:nth-child(4),
.items-table th.col-discount, .items-table td.col-discount,
 .po-items-table th.col-discount, .po-items-table td.col-discount { width: 20% !important; text-align: right !important; }

.sale-items-table th:nth-child(5), .sale-items-table td:nth-child(5),
.items-table th.col-total, .items-table td.col-total,
 .po-items-table th.col-total, .po-items-table td.col-total { width: 25% !important; text-align: right !important; }

/* Product Name Row spanning across the table */
.product-name-row {
    display: table-row !important;
    width: 100% !important;
}
.product-name-row td,
.product-name-row td.item-name,
.product-name-row td.name-col {
    display: table-cell !important;
    width: 100% !important;
    max-width: 100% !important;
    text-align: left !important;
    font-size: 11px !important;
    font-weight: 400 !important;
    color: #000 !important;
    padding: 3px 0 1px 0 !important;
    border-bottom: none !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.item-detail-row td {
    padding-top: 1px !important;
    padding-bottom: 3px !important;
}

/* 2-Column and 3-Column Tables (Gate Pass, Transfer, Supplier Receiving, Load Form) */
.gatepass-document table th:first-child, .gatepass-document table td:first-child,
.transfer-document table th:first-child, .transfer-document table td:first-child,
.supplier-document table th:first-child, .supplier-document table td:first-child {
    width: 12% !important;
    text-align: left !important;
}
.gatepass-document table th:nth-child(2), .gatepass-document table td:nth-child(2),
.transfer-document table th:nth-child(2), .transfer-document table td:nth-child(2),
.supplier-document table th:nth-child(2), .supplier-document table td:nth-child(2) {
    width: 58% !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
}
.gatepass-document table th:last-child, .gatepass-document table td:last-child,
.transfer-document table th:last-child, .transfer-document table td:last-child,
.supplier-document table th:last-child, .supplier-document table td:last-child {
    width: 30% !important;
    text-align: right !important;
    white-space: nowrap !important;
}

/* Totals and Grand Totals */
.total-line,
.grand-total,
.grand-total-row,
.total {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    max-width: 100% !important;
    background: #f3f4f6 !important;
    color: #000 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    padding: 3px 2px !important;
    margin: 2mm 0 !important;
    box-sizing: border-box !important;
}
.balance,
.balance-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    background: #fff3cd !important;
    border: 1px solid #ffc107 !important;
    padding: 3px 4px !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    color: #856404 !important;
    margin-top: 1.5mm !important;
    box-sizing: border-box !important;
}

/* Footer and QR Code */
.footer,
.footer-bar,
.footer-message,
.print-footer,
.receipt-footer {
    width: 100% !important;
    max-width: 100% !important;
    font-size: 10px !important;
    line-height: 1.35 !important;
    font-weight: 400 !important;
    color: #000 !important;
    white-space: pre-line !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    border-top: 1px solid #000 !important;
    margin-top: 2mm !important;
    padding-top: 1.5mm !important;
    text-align: left !important;
    box-sizing: border-box !important;
}
.footer *,
.footer-bar *,
.footer-message * {
    font-size: inherit !important;
    line-height: inherit !important;
    color: inherit !important;
}
.sale-payment-qr,
.qr-section {
    margin: 2.5mm auto 1mm !important;
    text-align: center !important;
    width: 100% !important;
}
.sale-payment-qr img,
.qr-section img,
img.qr-code,
img[class*="qr"] {
    display: block !important;
    width: 38mm !important;
    height: 38mm !important;
    max-width: 38mm !important;
    max-height: 38mm !important;
    margin: 0 auto 1mm !important;
    object-fit: contain !important;
    image-rendering: pixelated !important;
    background: #fff !important;
}
.sale-payment-qr-label,
.qr-label {
    font-size: 9.5px !important;
    font-weight: 600 !important;
    color: #000 !important;
    text-align: center !important;
}
.receiving-footer-separator,
.payment-footer-separator {
    display: none !important;
}

/* The Print Agent renders 80mm HTML with the Windows/GDI font mapper. Arial
   does not have distinct 500/600/800 faces there, so those weights can all
   rasterize as the same heavy face. Keep the thermal profile to the two
   weights that the renderer handles reliably and make the exceptions
   explicit. */
body.print-80mm,
body.print-80mm * {
    font-weight: 400 !important;
}
body.print-80mm .company,
body.print-80mm .company-name,
body.print-80mm .doc-title,
body.print-80mm .sale-invoice-title,
body.print-80mm .title,
body.print-80mm .document-title,
body.print-80mm .invoice-word,
body.print-80mm .section,
body.print-80mm .section-title,
body.print-80mm .info-label,
body.print-80mm .pm-title,
body.print-80mm thead th,
body.print-80mm .item-name,
body.print-80mm td.item-name,
body.print-80mm .line.bold,
body.print-80mm .line.bold *,
body.print-80mm .sum-row.bold,
body.print-80mm .sum-row.bold *,
body.print-80mm .total,
body.print-80mm .total-line,
body.print-80mm .grand-total,
body.print-80mm .grand-total-row,
body.print-80mm .balance,
body.print-80mm .balance-row,
body.print-80mm strong,
body.print-80mm b {
    font-weight: 700 !important;
}
`
        : isA5
          ? `
@page { size: A5 ${isA5Portrait ? 'portrait' : 'landscape'} !important; margin: 0 !important; }
.hr, .sep, .sep-dark, .rule { display: none !important; }
.header-rule { display: block !important; }
body { width: ${isA5Portrait ? '148mm' : '210mm'} !important; max-width: none !important; margin: 0 !important; padding: 2.5mm 6.5mm !important; font-size: 10px !important; line-height: 1.3 !important; }
.top-bar { display: none !important; }
.header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; }
.header-left { order: 1 !important; flex: 1 1 auto !important; text-align: left !important; display: flex !important; align-items: center !important; gap: 3mm !important; }
.header-right { order: 2 !important; flex: 0 0 38% !important; width: 38% !important; text-align: right !important; display: block !important; }
.logo, .logo-img { max-height: 14mm !important; max-width: 26mm !important; }
.company-name, .company { font-size: 15px !important; }
.company-meta, .company-sub, .subtitle, .branch-detail { font-size: 10px !important; }
.invoice-word, .title, .document-title, .doc-title, .subtitle { font-size: 15px !important; font-weight: 400 !important; }
.sale-invoice-title { font-size: 15px !important; font-weight: 400 !important; letter-spacing: 0 !important; }
.invoice-no, .number { font-size: 10px !important; font-weight: 400 !important; }
.doc-title, .section-title, .info-label, .pm-title, .section { font-size: 14px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; text-transform: uppercase !important; }
.line, .info-row, .pm-row, .sum-row { font-size: 12px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; }
table th { font-size: 12px !important; padding: 4px 5px !important; }
table td { font-size: 12px !important; line-height: 1.25 !important; padding: 4px 5px !important; }
.total-line, .grand-total, .grand-total-row { font-size: 15px !important; padding: 5px 5px !important; }
.footer, .footer-bar, .footer-message { font-size: 11px !important; }
`
          : `
@page { size: A4 portrait !important; margin: 0 !important; }
.hr, .sep, .sep-dark, .rule { display: none !important; }
.header-rule { display: block !important; }
body { width: 210mm !important; max-width: none !important; margin: 0 !important; padding: 5mm 6mm !important; font-size: 14px !important; line-height: 1.4 !important; }
body.sale-document.gatepass-document,
body.sale-document.gatepass-document .half,
body.sale-document.gatepass-document .page { min-height: 0 !important; height: auto !important; }
body.sale-document.gatepass-document .footer-bar { break-inside: auto !important; page-break-inside: auto !important; margin-top: 2mm !important; }
.top-bar { display: none !important; }
.header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; margin-bottom: 2mm !important; }
.header-left { order: 1 !important; flex: 1 1 auto !important; width: auto !important; text-align: left !important; display: flex !important; align-items: center !important; gap: 4mm !important; }
.header-right { order: 2 !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; text-align: right !important; display: block !important; padding-top: 1px !important; }
.logo, .logo-img { max-height: 14mm !important; max-width: 26mm !important; }
.company-name, .company { font-size: 21px !important; font-weight: 600 !important; }
.company-meta, .company-sub, .subtitle, .branch-detail { font-size: 11px !important; line-height: 1.3 !important; }
.invoice-word, .title, .document-title, .doc-title, .subtitle { font-size: 15px !important; font-weight: 600 !important; }
.sale-invoice-title { font-size: 15px !important; font-weight: 600 !important; letter-spacing: 0 !important; }
.invoice-no, .number { font-size: 10px !important; font-weight: 500 !important; letter-spacing: .5px !important; }
.doc-title, .section-title, .info-label, .pm-title, .section { font-size: 14px !important; font-weight: 800 !important; letter-spacing: 1px !important; text-transform: uppercase !important; }
.line, .info-row, .pm-row, .sum-row { font-size: 12px !important; padding: 1.5px 0 !important; }
table th { font-size: 12px !important; padding: 4px 5px !important; font-weight: 700 !important; background: #e5e5e5 !important; }
table td { font-size: 12px !important; padding: 4px 5px !important; font-weight: 500 !important; }
.total-line, .grand-total, .grand-total-row { font-size: 15px !important; padding: 5px !important; font-weight: 700 !important; background: #e5e5e5 !important; }
.footer, .footer-bar, .footer-message { font-size: 10px !important; line-height: 1.25 !important; }
`
}
body.sale-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; }
body.sale-document .top-bar { display: none !important; }
body.sale-document .header-rule { display: block !important; border-top: 1px dashed #64748b !important; margin-bottom: 3mm !important; }
body.sale-document .header-row > .header-left { order: 1 !important; flex: 1 1 auto !important; width: auto !important; text-align: left !important; display: flex !important; align-items: center !important; }
body.sale-document .header-row > .header-right { order: 2 !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; text-align: right !important; display: block !important; padding-top: 1px !important; }
body.sale-document .header-right > .invoice-word,
body.sale-document .header-right > .invoice-no,
body.sale-document .header-right > .logo-img,
body.sale-document .header-right > .company-name { display: none !important; }
body.sale-document .header-left .company-name { font-family: Arial, Helvetica, sans-serif !important; font-size: 21px !important; font-weight: 600 !important; color: #374151 !important; text-transform: none !important; line-height: 1.05 !important; }
body.sale-document .header-left .invoice-word { font-family: Arial, Helvetica, sans-serif !important; font-size: 15px !important; font-weight: 400 !important; letter-spacing: 0 !important; text-transform: none !important; line-height: 1.2 !important; }
body.sale-document .header-left .invoice-word { display: block !important; }
body.sale-document.gatepass-document .header-left .invoice-word { display: block !important; }
body.sale-document.gatepass-document table tbody tr:last-child { border-bottom: 0 !important; }
body.sale-document.order-request-document .top-bar { display: none !important; }
body.sale-document.order-request-document .header-rule { display: block !important; }
body.sale-document.order-request-document .section-title { border-bottom: 0 !important; padding-bottom: 0 !important; }
body.sale-document.order-request-document table tbody tr:last-child { border-bottom: 0 !important; }
body.sale-document.purchase-order-document .half,
body.sale-document.purchase-order-document .page { min-height: 0 !important; height: auto !important; }
body.sale-document.purchase-order-document .header-left {
    display: grid !important;
    grid-template-columns: auto minmax(0, 1fr) !important;
    grid-template-rows: auto auto auto !important;
    align-items: start !important;
    column-gap: 4mm !important;
    row-gap: 1px !important;
}
body.sale-document.purchase-order-document .header-left .logo-img { grid-column: 1 !important; grid-row: 1 / span 3 !important; }
body.sale-document.purchase-order-document .header-left .company-name { grid-column: 2 !important; grid-row: 1 !important; }
body.sale-document.purchase-order-document .header-left .invoice-word { grid-column: 2 !important; grid-row: 2 !important; display: block !important; margin-top: 1px !important; }
body.sale-document.purchase-order-document .header-left .invoice-no { grid-column: 2 !important; grid-row: 3 !important; display: block !important; margin-top: 1px !important; letter-spacing: .5px !important; }
body.sale-document.purchase-order-document .footer-bar {
    break-before: auto !important;
    page-break-before: auto !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    margin-top: 2mm !important;
}
body.sale-document.load-form-document .top-bar { display: none !important; }
body.sale-document.load-form-document .header-rule { display: block !important; }
body.sale-document.load-form-document .page { padding: 0 !important; }
body.sale-document.load-form-document { padding: 5mm 6mm !important; }
body.sale-document.load-form-document table { border-bottom: 0 !important; margin-bottom: 1mm !important; }
body.sale-document.load-form-document .footer { border-top: 0 !important; margin-top: 1mm !important; padding-top: 1mm !important; }
body.sale-document.load-form-document table tbody tr:last-child { border-bottom: 0 !important; }
body.sale-document.load-form-document .summary { border-top: 0 !important; margin-top: 0 !important; }
body.sale-document.load-form-document table { margin-bottom: 0 !important; }
body.sale-document.load-form-document .section { border-bottom: 0 !important; }
body.sale-document.load-form-document table { border-bottom: 0 !important; }
body.sale-document.load-form-document .summary { border-bottom: 0 !important; }
body.sale-document.refund-document .header-row { display: flex !important; flex-direction: row !important; }
body.sale-document.refund-document .header-left { order: 1 !important; flex: 1 1 auto !important; width: auto !important; display: flex !important; flex-direction: row !important; align-items: center !important; justify-content: flex-start !important; text-align: left !important; gap: 4mm !important; }
body.sale-document.refund-document .header-right { order: 2 !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; display: block !important; text-align: right !important; padding-top: 1px !important; }
body.sale-document.refund-document .brand-details { display: flex !important; flex-direction: column !important; align-items: flex-start !important; text-align: left !important; }
body.sale-document.refund-document .header-left .logo { display: block !important; margin: 0 !important; }
body.sale-document.refund-document .header-left .company-name { font-size: 21px !important; font-weight: 600 !important; }
body.sale-document.refund-document .header-left .invoice-word { font-size: 15px !important; font-weight: 400 !important; font-family: Arial, Helvetica, sans-serif !important; }
body.sale-document.refund-document .header-left .invoice-no { font-size: 10px !important; font-weight: 400 !important; }
body.sale-document.refund-document .header-left .logo-img { max-height: 14mm !important; max-width: 26mm !important; }
body.sale-document.refund-document .header-right .company-meta { font-size: 11px !important; font-weight: 600 !important; }
body.sale-document.refund-document .header-right .header-tax { font-size: 9px !important; }
body.sale-document.refund-document table thead th { font-size: 12px !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.sale-document.refund-document table tbody td { font-size: 12px !important; padding: 3px 5px !important; font-weight: 400 !important; }
body.sale-document.refund-document .line { font-size: 12px !important; padding: 1.5px 0 !important; }
body.sale-document.refund-document .line,
body.sale-document.refund-document .line span:first-child { font-weight: 400 !important; }
body.sale-document.refund-document .reason-row { margin-top: 1mm !important; }
body.sale-document.refund-document .no-exchange { font-size: 14px !important; font-weight: 600 !important; padding: 3px 0 !important; }
body.sale-document.refund-document .section-title { font-size: 14px !important; border-bottom: 0 !important; padding-bottom: 0 !important; }
body.sale-document.refund-document .total-line { font-size: 15px !important; padding: 5px !important; font-weight: 600 !important; }
body.sale-document.refund-document .footer { font-size: 11px !important; border-top: 0 !important; }
body.sale-document .header-left .invoice-no { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; font-weight: 400 !important; letter-spacing: .3px !important; text-align: left !important; line-height: 1.2 !important; }
body.sale-document .header-left .logo-img { max-height: 14mm !important; max-width: 26mm !important; margin: 0 !important; }
body.sale-document .header-right .company-sub { font-family: Arial, Helvetica, sans-serif !important; font-size: 11px !important; font-weight: 600 !important; color: #111827 !important; text-align: right !important; line-height: 1.3 !important; letter-spacing: 0 !important; text-transform: none !important; margin-top: 2px !important; }
body.sale-document .header-right .company-sub.header-tax { font-size: 9px !important; font-weight: 600 !important; margin-top: 3px !important; }
body.sale-document .info-row,
body.sale-document .info-row .lbl,
body.sale-document .info-row span:last-child,
body.sale-document .pm-row,
body.sale-document .pm-row .pm-lbl,
body.sale-document .pm-row span:last-child { font-family: Arial, Helvetica, sans-serif !important; font-size: 14px !important; font-weight: 400 !important; line-height: 1.25 !important; letter-spacing: 0 !important; text-transform: none !important; }
body.sale-document .info-row,
body.sale-document .info-row .lbl,
body.sale-document .info-row span:last-child,
body.sale-document .pm-row,
body.sale-document .pm-row .pm-lbl,
body.sale-document .pm-row span:last-child { font-size: 12px !important; }
body.sale-document table thead th { font-family: Arial, Helvetica, sans-serif !important; font-size: 12px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body.sale-document table tbody td { font-family: Arial, Helvetica, sans-serif !important; font-size: 12px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body.sale-document .info-label,
body.sale-document .pm-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; letter-spacing: .5px !important; }
body.sale-document .info-label { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; letter-spacing: .5px !important; }
body.sale-document .customer-name { font-size: 15px !important; line-height: 1.2 !important; }
body.sale-document .sum-row,
body.sale-document .total-items { font-size: 14px !important; line-height: 1.3 !important; font-weight: 700 !important; }
body.sale-document .sum-row { font-size: 13px !important; line-height: 1.3 !important; font-weight: 500 !important; color: #111827 !important; }
body.sale-document .sum-row.bold { font-weight: 500 !important; }
body.sale-document .sum-row.bold .sum-lbl { font-weight: 700 !important; }
body.sale-document .sum-lbl { color: #111827 !important; }
body.sale-document .total-items { font-size: 14px !important; font-weight: 700 !important; line-height: 1.3 !important; }
body.purchase-order-document table tbody tr:last-child { border-bottom: 0 !important; }
body.purchase-order-document .total-items-row { border-top: 0 !important; }
body.purchase-order-document .grand-total { display: flex !important; justify-content: space-between !important; align-items: center !important; border-top: 0 !important; margin-top: 4px !important; margin-bottom: 3px !important; padding: 7px 9px !important; }
body.purchase-order-document .total-line,
body.purchase-order-document .grand-total-row { border-top: 0 !important; }
body.purchase-order-document .sum-row + .total-line,
body.purchase-order-document .sum-row + .grand-total-row { border-top: 0 !important; }
body.purchase-order-document .grand-total span { display: block !important; margin-left: 0 !important; }
body.purchase-order-document .grand-total span:last-child { margin-left: auto !important; text-align: right !important; }
body.purchase-order-document .po-extra-tax-row,
body.purchase-order-document .po-paid-amount-row { border-bottom: 0 !important; }
body.purchase-order-document .paid-amount-row { padding: 4px 9px !important; }
body.purchase-order-document.sale-a5 .grand-total { margin-top: 2px !important; margin-bottom: 2px !important; padding: 5px !important; }
body.purchase-order-document.sale-a5 .paid-amount-row { padding: 3px 5px !important; }
body.purchase-order-document .po-header-separator { border-top: 1px dotted #64748b !important; margin: 1mm 0 2mm !important; height: 0 !important; }
body.purchase-order-document .footer-bar { border-top: 1px solid #000 !important; margin-top: 1mm !important; padding-top: 1mm !important; }
body.purchase-order-document .header-left { row-gap: 0 !important; }
body.purchase-order-document .header-left .invoice-word,
body.purchase-order-document .header-left .invoice-no { margin-top: 0 !important; line-height: 1.1 !important; }
body.purchase-order-document .po-header-separator { display: block !important; border-top: 1px dotted #64748b !important; height: 0 !important; margin: 1mm 0 2mm !important; }
body.purchase-order-document .sep,
body.purchase-order-document .summary-separator { display: none !important; }
body.purchase-order-document .info-label { font-size: 13px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .4px !important; margin-top: 2mm !important; margin-bottom: 1mm !important; border: 0 !important; padding-bottom: 0 !important; }
body.purchase-order-document .info-strip + .sep { display: block !important; border: 0 !important; border-top: 1px solid #000 !important; height: 0 !important; margin: 1mm 0 2mm !important; }
body.purchase-order-document .po-items-table { table-layout: fixed !important; width: 100% !important; }
body.purchase-order-document .po-items-table th,
body.purchase-order-document .po-items-table td { font-size: 10px !important; line-height: 1.15 !important; padding: 2px 4px !important; font-weight: 500 !important; overflow-wrap: normal !important; word-break: normal !important; }
body.purchase-order-document .po-items-table th { font-weight: 700 !important; }
body.purchase-order-document .po-items-table th:nth-child(1), body.purchase-order-document .po-items-table td:nth-child(1) { width: 5% !important; }
body.purchase-order-document .po-items-table th:nth-child(2), body.purchase-order-document .po-items-table td:nth-child(2) { width: 37% !important; text-align: left !important; }
body.purchase-order-document .po-items-table th:nth-child(3), body.purchase-order-document .po-items-table td:nth-child(3) { width: 13% !important; }
body.purchase-order-document .po-items-table th:nth-child(4), body.purchase-order-document .po-items-table td:nth-child(4) { width: 14% !important; }
body.purchase-order-document .po-items-table th:nth-child(5), body.purchase-order-document .po-items-table td:nth-child(5) { width: 14% !important; }
body.purchase-order-document .po-items-table th:nth-child(6), body.purchase-order-document .po-items-table td:nth-child(6) { width: 8% !important; }
body.purchase-order-document .po-items-table th:nth-child(7), body.purchase-order-document .po-items-table td:nth-child(7) { width: 9% !important; }
body.purchase-order-document table { border-bottom: 0 !important; margin-bottom: 1mm !important; }
body.purchase-order-document table tbody tr:last-child td { border-bottom: 0 !important; }
body.purchase-order-document .total-items-row { display: grid !important; grid-template-columns: minmax(0, 1fr) max-content !important; column-gap: 8mm !important; align-items: center !important; margin-top: 0 !important; margin-bottom: 1mm !important; padding-top: 2px !important; padding-bottom: 2px !important; border-top: 0 !important; border-bottom: 0 !important; }
body.purchase-order-document .total-items-row span:last-child { margin-left: 0 !important; padding-left: 0 !important; text-align: right !important; white-space: nowrap !important; }
body.transfer-document table tbody tr:last-child { border-bottom: 0 !important; }
body.transfer-document .transfer-items-table thead th,
body.transfer-document table thead th { border-top: 0 !important; border-bottom: 0 !important; }
body.transfer-document .total-items { border-top: 0 !important; border-bottom: 1px solid #6b7280 !important; margin-top: 2px !important; padding-top: 3px !important; padding-bottom: 3px !important; }
body.supplier-document .info-label { border-bottom: 0 !important; padding-bottom: 0 !important; }
body.supplier-document table tbody tr:last-child { border-bottom: 0 !important; }
body.supplier-document .grand-total { border-bottom: 0 !important; margin-bottom: 0 !important; }
body.supplier-document .footer-bar { border-top: 0 !important; }
body.supplier-document .footer-bottom { border-top: 0 !important; }
body.supplier-document .footer { border-top: 0 !important; margin-top: 2mm !important; }
body.supplier-document .total-line { border-bottom: 0 !important; font-weight: 600 !important; }
body.supplier-document .payment-summary .sum-row,
body.supplier-document .payment-summary .grand-total,
body.supplier-document .payment-summary-row,
body.supplier-document .payment-total-row.total-line,
body.supplier-document .receiving-summary .total-line,
body.supplier-document .receiving-summary-row.total-line { display: grid !important; grid-template-columns: minmax(0, 1fr) max-content !important; align-items: center !important; column-gap: 8mm !important; width: 100% !important; }
body.supplier-document .payment-summary .sum-row > :last-child,
body.supplier-document .payment-summary .grand-total > :last-child,
body.supplier-document .payment-summary-row > :last-child,
body.supplier-document .payment-total-row.total-line > :last-child,
body.supplier-document .receiving-summary .total-line > :last-child,
body.supplier-document .receiving-summary-row.total-line > :last-child { min-width: 0 !important; text-align: right !important; white-space: nowrap !important; }
body.supplier-document .receiving-footer-separator,
body.supplier-document .payment-footer-separator { display: none !important; }
body.supplier-document .receiving-footer-separator + .footer-bar,
body.supplier-document .payment-footer-separator + .footer-bar { margin-top: 0 !important; border-top: 0 !important; }
body.sale-document .grand-total { font-size: 15px !important; font-weight: 600 !important; }
body.sale-document .summary-block { flex: 0 0 105mm !important; width: auto !important; min-width: 0 !important; margin-left: auto !important; }
body.sale-document.sale-a5 .summary-block { flex-basis: 78mm !important; }
body.sale-document .balance,
body.sale-document .balance-row { display: flex !important; justify-content: space-between !important; background: #fff3cd !important; border: 1px solid #ffc107 !important; padding: 5px 7px !important; font-size: 10px !important; font-weight: 700 !important; color: #856404 !important; margin-top: 2px !important; }
body.sale-document .footer,
body.sale-document .footer-bar { font-size: 11px !important; line-height: 1.35 !important; }
body.sale-document .footer-message { font-size: inherit !important; line-height: inherit !important; }
/* Return/exchange A4 and A5 documents follow the Sale Invoice geometry. */
body.sale-document.refund-document { color: #111827 !important; background: #fff !important; }
body.sale-document.refund-document .header-row { margin-bottom: 2mm !important; }
body.sale-document.refund-document .header-left { display: flex !important; align-items: center !important; gap: 4mm !important; text-align: left !important; }
body.sale-document.refund-document .header-right { width: 38% !important; max-width: 38% !important; text-align: right !important; }
body.sale-document.refund-document .header-rule { border-top: 1px dashed #64748b !important; margin-bottom: 3mm !important; }
body.sale-document.refund-document .info-strip { display: flex !important; width: 100% !important; gap: 8mm !important; margin-bottom: 3mm !important; }
body.sale-document.refund-document .info-block { flex: 1 1 0 !important; min-width: 0 !important; }
body.sale-document.refund-document table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin-bottom: 3mm !important; }
body.sale-document.refund-document table thead th { background: #f3f4f6 !important; color: #111827 !important; border: 1px solid #d1d5db !important; font-weight: 700 !important; }
body.sale-document.refund-document table tbody td { color: #343a40 !important; border-bottom: 1px solid #000 !important; }
body.sale-document.refund-document .total-line { width: 100% !important; background: #f3f4f6 !important; color: #111827 !important; border-top: 1px solid #333 !important; border-bottom: 1px solid #333 !important; }
body.sale-document.refund-document .footer { width: 100% !important; border-top: 1px solid #343a40 !important; padding-top: 1mm !important; }
/* Keep A5 PO/transfer/sale footers on the first page in browser print. */
body.sale-document.sale-a5 .page { padding-bottom: 0 !important; }
body.sale-document.sale-a5 .footer-bar { margin-top: 1mm !important; padding-top: 1mm !important; break-before: avoid !important; page-break-before: avoid !important; break-inside: avoid !important; page-break-inside: avoid !important; }
body.sale-document.sale-a5 .footer-message { font-size: 8px !important; line-height: 1.15 !important; }
body.sale-document.sale-a5 .footer-bottom { margin-top: 0 !important; }
body.sale-document .pm-row,
body.sale-document .pm-row .pm-lbl,
body.sale-document .pm-row span:last-child { font-family: Arial, Helvetica, sans-serif !important; font-size: 16px !important; font-weight: 400 !important; line-height: 1.25 !important; }
/* 80mm receipts use one footer divider only. */
body.print-80mm[class][class][class] .receiving-footer-separator,
body.print-80mm[class][class][class] .payment-footer-separator { display: none !important; border: 0 !important; height: 0 !important; margin: 0 !important; padding: 0 !important; }
body.print-80mm[class][class][class] .footer,
body.print-80mm[class][class][class] .footer-bar { border-top: 1px solid #000 !important; margin-top: 2mm !important; padding-top: 1.5mm !important; break-before: avoid !important; page-break-before: avoid !important; break-inside: avoid !important; page-break-inside: avoid !important; }
body.print-80mm[class][class][class] .footer-message,
body.print-80mm[class][class][class] .print-footer,
body.print-80mm[class][class][class] .receipt-footer { border-top: 0 !important; margin-top: 0 !important; }

/* Canonical 80mm Sale typography. Keep every ordinary row regular; only the
   brand, document title, and grand total are emphasized. This final block is
   intentionally last so it wins over document-specific legacy CSS above. */
body.print-80mm.sale-pos-receipt[class][class][class],
body.print-80mm.sale-pos-receipt[class][class][class] * {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-weight: 400 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .company,
body.print-80mm.sale-pos-receipt[class][class][class] .company-name,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-invoice-title,
body.print-80mm.sale-pos-receipt[class][class][class] .doc-title,
body.print-80mm.sale-pos-receipt[class][class][class] .invoice-word,
body.print-80mm.sale-pos-receipt[class][class][class] .section,
body.print-80mm.sale-pos-receipt[class][class][class] .section-title,
body.print-80mm.sale-pos-receipt[class][class][class] .info-label,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-title,
body.print-80mm.sale-pos-receipt[class][class][class] table thead th,
body.print-80mm.sale-pos-receipt[class][class][class] .grand-total,
body.print-80mm.sale-pos-receipt[class][class][class] .grand-total-row,
body.print-80mm.sale-pos-receipt[class][class][class] .total-line {
    font-weight: 700 !important;
    text-transform: uppercase !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .company-meta,
body.print-80mm.sale-pos-receipt[class][class][class] .company-sub,
body.print-80mm.sale-pos-receipt[class][class][class] .branch-detail,
body.print-80mm.sale-pos-receipt[class][class][class] .header-address,
body.print-80mm.sale-pos-receipt[class][class][class] .header-tax,
body.print-80mm.sale-pos-receipt[class][class][class] .print-details,
body.print-80mm.sale-pos-receipt[class][class][class] .line,
body.print-80mm.sale-pos-receipt[class][class][class] .info-row,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-row,
body.print-80mm.sale-pos-receipt[class][class][class] .sum-row,
body.print-80mm.sale-pos-receipt[class][class][class] table tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .footer,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-bar,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-message,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-payment-qr-label {
    text-transform: none !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table {
    table-layout: fixed !important;
    width: 100% !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: hidden !important;
    text-overflow: clip !important;
    white-space: nowrap !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(4) {
    font-size: 11px !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(1) { width: 16% !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(2) { width: 14% !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(3) { width: 26% !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(4) { width: 16% !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td:nth-child(5) { width: 28% !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table .product-name-row td {
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
}
/* Final 80mm sale-table geometry and compact thermal typography. */
body.print-80mm .product-name,
body.print-80mm .item-name,
body.print-80mm .product-name-row td {
    font-size: 10.5px !important;
    font-weight: 700 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: clip !important;
    overflow-wrap: normal !important;
}
body.print-80mm .grand-total-row,
body.print-80mm .net-total-row {
    font-size: 12px !important;
    font-weight: 700 !important;
}
body.print-80mm .table-cell.tax,
body.print-80mm .table-cell.disc,
body.print-80mm .table-cell.discount,
body.print-80mm .col-tax,
body.print-80mm .col-discount,
body.print-80mm .col-disc {
    min-width: 58px !important;
    white-space: nowrap !important;
}
body.print-80mm .table-row,
body.print-80mm .sale-items-table tbody tr,
body.print-80mm .items-table tbody tr {
    line-height: 1.15 !important;
}
body.print-80mm .info-row {
    padding-top: 0.5px !important;
    padding-bottom: 0.5px !important;
}
body.print-80mm .table-col-rate,
body.print-80mm .col-rate { width: 18% !important; text-align: right !important; }
body.print-80mm .table-col-qty,
body.print-80mm .col-qty { width: 12% !important; text-align: center !important; }
body.print-80mm .table-col-tax,
body.print-80mm .col-tax { width: 25% !important; text-align: center !important; }
body.print-80mm .table-col-disc,
body.print-80mm .table-col-discount,
body.print-80mm .col-disc,
body.print-80mm .col-discount { width: 20% !important; text-align: right !important; }
body.print-80mm .table-col-total,
body.print-80mm .col-total { width: 25% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table .product-name-row td,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.item-name {
    font-size: 10.5px !important;
    font-weight: 700 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: clip !important;
    overflow-wrap: normal !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-rate,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.col-rate { width: 18% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-qty,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.col-qty { width: 12% !important; text-align: center !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-tax,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.col-tax {
    width: 25% !important; text-align: center !important; white-space: nowrap !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-discount,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.col-discount {
    width: 20% !important; min-width: 58px !important; text-align: right !important; white-space: nowrap !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-total,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.col-total { width: 25% !important; text-align: right !important; }
/* Compact Sale receipt profile: matches the smaller reference thermal print. */
body.print-80mm.sale-pos-receipt[class][class][class],
body.print-80mm.sale-pos-receipt[class][class][class] * {
    font-family: Arial, Helvetica, sans-serif !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] {
    font-size: 10px !important;
    line-height: 1.2 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .header,
body.print-80mm.sale-pos-receipt[class][class][class] .brand {
    margin-bottom: 1.5mm !important;
    padding-bottom: 1.5mm !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .logo,
body.print-80mm.sale-pos-receipt[class][class][class] .logo-img {
    max-height: 16mm !important;
    margin-bottom: 1mm !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .company,
body.print-80mm.sale-pos-receipt[class][class][class] .company-name {
    font-size: 15px !important;
    line-height: 1.05 !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .company-meta,
body.print-80mm.sale-pos-receipt[class][class][class] .company-sub,
body.print-80mm.sale-pos-receipt[class][class][class] .branch-detail,
body.print-80mm.sale-pos-receipt[class][class][class] .header-address,
body.print-80mm.sale-pos-receipt[class][class][class] .header-tax {
    font-size: 8.5px !important;
    line-height: 1.15 !important;
    margin-top: 0 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-invoice-title,
body.print-80mm.sale-pos-receipt[class][class][class] .invoice-word,
body.print-80mm.sale-pos-receipt[class][class][class] .doc-title,
body.print-80mm.sale-pos-receipt[class][class][class] .title {
    font-size: 11px !important;
    line-height: 1.1 !important;
    margin: 1mm 0 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .invoice-no,
body.print-80mm.sale-pos-receipt[class][class][class] .number {
    font-size: 8.5px !important;
    line-height: 1.15 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .section,
body.print-80mm.sale-pos-receipt[class][class][class] .section-title,
body.print-80mm.sale-pos-receipt[class][class][class] .info-label,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-title {
    font-size: 9.5px !important;
    line-height: 1.1 !important;
    margin: 1mm 0 0.5mm !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .line,
body.print-80mm.sale-pos-receipt[class][class][class] .info-row,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-row,
body.print-80mm.sale-pos-receipt[class][class][class] .sum-row {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
    padding-top: 0.5px !important;
    padding-bottom: 0.5px !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
    padding: 1.5px 1px !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th {
    font-size: 9.5px !important;
    font-weight: 700 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table .product-name-row td,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.item-name {
    font-size: 9.5px !important;
    font-weight: 700 !important;
}
/* The muted line under a product's name (family, SKU, material, colour,
   size, type, dimensions — the Receipts-tab switches): its own block under
   the bold name, small and regular, wrapping where the name may not. */
body.print-80mm .product-name-row td .item-detail,
body.print-80mm td.item-name .item-detail,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table .product-name-row td .item-detail,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td.item-name .item-detail {
    display: block !important;
    font-size: 8px !important;
    font-weight: 400 !important;
    color: #333 !important;
    line-height: 1.15 !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: anywhere !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .grand-total,
body.print-80mm.sale-pos-receipt[class][class][class] .grand-total-row,
body.print-80mm.sale-pos-receipt[class][class][class] .total-line {
    font-size: 11px !important;
    line-height: 1.15 !important;
    padding: 2px 3px !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .footer,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-bar,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-message {
    font-size: 8.5px !important;
    line-height: 1.15 !important;
}
/* One table scale for every 80mm Print Agent document (sale, purchase,
   receiving, transfer, refund, gate pass, and order request). Document-level
   table rules above must not make section headers larger than their rows. */
body.print-80mm[class][class][class] table,
body.print-80mm[class][class][class] table th,
body.print-80mm[class][class][class] table td,
body.print-80mm[class][class][class] table th *,
body.print-80mm[class][class][class] table td * {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
}
body.print-80mm[class][class][class] table th {
    font-weight: 700 !important;
    padding-top: 1.5px !important;
    padding-bottom: 1.5px !important;
}
body.print-80mm[class][class][class] table td {
    font-weight: 400 !important;
    padding-top: 1.5px !important;
    padding-bottom: 1.5px !important;
}
body.print-80mm[class][class][class] table td.item-name,
body.print-80mm[class][class][class] table .product-name,
body.print-80mm[class][class][class] table .product-name-row td {
    font-size: 9.5px !important;
    font-weight: 700 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: clip !important;
}
/* Shared 80mm document frame: Sale, Purchase, Supplier, Transfer, Load Form,
   Order Request, Return/Refund, Exchange, and Gate Pass. */
body.print-80mm[class][class][class] .header-row,
body.print-80mm[class][class][class] .brand-row,
body.print-80mm[class][class][class] .print-header {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: 100% !important;
    gap: 0 !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    text-align: center !important;
}
body.print-80mm[class][class][class] .header-left,
body.print-80mm[class][class][class] .header-right,
body.print-80mm[class][class][class] .brand-left,
body.print-80mm[class][class][class] .brand-right {
    display: block !important;
    flex: none !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    text-align: center !important;
}
body.print-80mm[class][class][class] .brand-details,
body.print-80mm[class][class][class] .header-left .logo,
body.print-80mm[class][class][class] .header-left .logo-img,
body.print-80mm[class][class][class] .brand-left .logo,
body.print-80mm[class][class][class] .brand-left .logo-img {
    margin-left: auto !important;
    margin-right: auto !important;
    text-align: center !important;
}
/* POS Sale stores the document title in the left block and company details in
   the right block, so put the company block first like every other receipt. */
body.print-80mm.sale-pos-receipt:not(.gatepass-document):not(.refund-document):not(.exchange-document):not(.supplier-document):not(.purchase-order-document):not(.transfer-document):not(.load-form-document):not(.order-request-document) .header-right {
    order: 1 !important;
}
body.print-80mm.sale-pos-receipt:not(.gatepass-document):not(.refund-document):not(.exchange-document):not(.supplier-document):not(.purchase-order-document):not(.transfer-document):not(.load-form-document):not(.order-request-document) .header-left {
    order: 2 !important;
}
/* Exactly one separator after the header details (address + NTN/STRN). */
body.print-80mm[class][class][class] .header-rule,
body.print-80mm[class][class][class] .sep-dark,
body.print-80mm[class][class][class] .po-header-separator,
body.print-80mm[class][class][class] .header-row + .hr {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1.5mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px dashed #000 !important;
}
body.print-80mm[class][class][class] .header,
body.print-80mm[class][class][class] .brand,
body.print-80mm[class][class][class] .print-header {
    border-bottom: 0 !important;
}
/* Remove extra separator elements before the single footer rule. */
body.print-80mm[class][class][class] .receiving-footer-separator,
body.print-80mm[class][class][class] .payment-footer-separator {
    display: none !important;
    border: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}
body.print-80mm[class][class][class] .footer,
body.print-80mm[class][class][class] .footer-bar {
    border-top: 1px solid #000 !important;
    margin-top: 1.5mm !important;
    padding-top: 1mm !important;
}
body.print-80mm[class][class][class] .footer-message,
body.print-80mm[class][class][class] .footer-bottom,
body.print-80mm[class][class][class] .print-footer,
body.print-80mm[class][class][class] .receipt-footer {
    border-top: 0 !important;
    margin-top: 0 !important;
}
/* Compact, uniform Sale headings: RATE / QTY / TAX / DISC / TOTAL. */
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th.col-rate,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th.col-qty,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th.col-tax,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th.col-discount,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th.col-total {
    font-size: 9px !important;
    line-height: 1.1 !important;
    font-weight: 700 !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
}
/* The POS Sale table has no col-* classes, so lock its five columns by
   position. Headers and values now share exactly the same edges. */
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(1) { width: 18% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(2) { width: 10% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(3) { width: 24% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(4) { width: 20% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(5) { width: 28% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class] hr.sep-dark {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1.5mm 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
/* Product names must remain fully readable. Long names wrap naturally instead
   of being clipped on Sale, PO, Return/Refund, Supplier, Transfer, Load Form,
   Order Request, and Gate Pass receipts. */
body.print-80mm[class][class][class] table td.item-name,
body.print-80mm[class][class][class] table .product-name,
body.print-80mm[class][class][class] table .product-name-row td,
body.print-80mm[class][class][class] table td.name-col {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
    white-space: normal !important;
    overflow: visible !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    text-overflow: clip !important;
}
/* Final 80mm Sale overrides. These selectors intentionally match the
   high-specificity rules above so the Print Agent cannot restore the old
   18/10/24/20/28 geometry or hide the header separator. */
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr:not(.product-name-row) td:nth-child(5) {
    width: 20% !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .header + .hr {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 2mm !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
/* Final 80mm Purchase Order corrections. */
body.print-80mm.purchase-order-document[class][class][class] .po-header-separator {
    display: block !important;
    height: 0 !important;
    margin: 0.5mm 0 1mm !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
body.print-80mm.purchase-order-document[class][class][class] .header-row {
    margin-bottom: 0 !important;
}
body.print-80mm.purchase-order-document[class][class][class] .po-items-table tr.product-name-row td,
body.print-80mm.purchase-order-document[class][class][class] .po-items-table td.item-name {
    white-space: normal !important;
    overflow: visible !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    text-overflow: clip !important;
}
body.print-80mm.purchase-order-document[class][class][class] .summary-block hr.summary-separator {
    display: none !important;
}
body.print-80mm.purchase-order-document[class][class][class] .summary-block hr.summary-separator:last-of-type {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
/* Final shared 80mm contract: never clip a long item name.  Several older
   document rules use nowrap/hidden with higher specificity, so this must be
   the last rule in the injected stylesheet. */
body.print-80mm[class][class][class] table td.item-name,
body.print-80mm[class][class][class] table .product-name,
body.print-80mm[class][class][class] table .product-name-row td,
body.print-80mm[class][class][class] table td.name-col {
    min-width: 0 !important;
    max-width: 100% !important;
    white-space: normal !important;
    overflow: visible !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    text-overflow: clip !important;
}
/* Keep the header structure consistent across all 80mm documents. */
body.print-80mm[class][class][class] .header + .hr,
body.print-80mm[class][class][class] .header-row + .hr,
body.print-80mm[class][class][class] .header-rule,
body.print-80mm[class][class][class] .po-header-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
body.print-80mm[class][class][class] .doc-title,
body.print-80mm[class][class][class] .sale-invoice-title,
body.print-80mm[class][class][class] .invoice-word {
    margin-top: 1mm !important;
    margin-bottom: 1mm !important;
}
/* Exactly one footer divider: the footer owns the line; intermediate
   separator elements cannot create a second line. */
body.print-80mm[class][class][class] .footer,
body.print-80mm[class][class][class] .footer-bar {
    border-top: 1px solid #000 !important;
    margin-top: 1mm !important;
    padding-top: 1mm !important;
}
body.print-80mm[class][class][class] .footer-message,
body.print-80mm[class][class][class] .footer-bottom,
body.print-80mm[class][class][class] .print-footer,
body.print-80mm[class][class][class] .receipt-footer {
    border-top: 0 !important;
}
body.print-80mm[class][class][class] .payment-footer-separator,
body.print-80mm[class][class][class] .receiving-footer-separator,
body.print-80mm[class][class][class] .footer-separator,
body.print-80mm[class][class][class] .footer-divider {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
}
/* Gate Pass: one-pixel item-table rule, with no bottom rule on the last row. */
body.print-80mm.gatepass-document[class][class][class] table thead th,
body.print-80mm.gatepass-document[class][class][class] table thead th.gatepass-th {
    border-bottom: 1px solid #000 !important;
}
body.print-80mm.gatepass-document[class][class][class] table tbody tr:last-child td {
    border-bottom: 0 !important;
}
/* The receiving voucher's item heading is redundant beside its table header. */
body.print-80mm[class][class][class] .receiving-items-title {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
}
/* Sale 80mm final cleanup: one divider after NTN/STRN, no dotted item-row
   rules, uniform Tax/Disc header size, heavier preview text,
   and one solid separator owned by Net Total. */
body.print-80mm.sale-pos-receipt[class][class][class] .header + .hr,
body.print-80mm.sale-pos-receipt[class][class][class] .print-header + .hr,
body.print-80mm.sale-pos-receipt[class][class][class] .header-rule {
    display: block !important;
    visibility: visible !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class],
body.print-80mm.sale-pos-receipt[class][class][class] .line,
body.print-80mm.sale-pos-receipt[class][class][class] .info-row,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-row,
body.print-80mm.sale-pos-receipt[class][class][class] .sum-row,
body.print-80mm.sale-pos-receipt[class][class][class] table tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .footer,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-bar,
body.print-80mm.sale-pos-receipt[class][class][class] .footer-message {
    color: #000 !important;
    font-weight: 400 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .company,
body.print-80mm.sale-pos-receipt[class][class][class] .company-name,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-invoice-title,
body.print-80mm.sale-pos-receipt[class][class][class] .doc-title,
body.print-80mm.sale-pos-receipt[class][class][class] .section-title,
body.print-80mm.sale-pos-receipt[class][class][class] .info-label,
body.print-80mm.sale-pos-receipt[class][class][class] .pm-title,
body.print-80mm.sale-pos-receipt[class][class][class] table thead th {
    color: #000 !important;
    font-weight: 500 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list td,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th *,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table td *,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th *,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list td * {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list thead th,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-tax,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-discount,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th.col-disc,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class] .item-list th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list tbody td {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table thead th,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list thead th {
    font-weight: 500 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list tbody td {
    font-weight: 400 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table tbody tr,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list tbody td,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list tbody tr {
    border-bottom: 0 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-items-table + .hr,
body.print-80mm.sale-pos-receipt[class][class][class] .item-list + .hr {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .net-total-row {
    border-bottom: 1px solid #000 !important;
    padding-bottom: 1px !important;
    margin-bottom: 1px !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .net-total-row + .hr {
    display: none !important;
}
body.print-80mm.sale-document[class][class][class] .net-total-row {
    border-bottom: 1px solid #000 !important;
}
body.print-80mm.sale-document[class][class][class] .tax-details-row {
    border-bottom: 0 !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-payment-qr img,
body.print-80mm.sale-pos-receipt[class][class][class] .qr-section img,
body.print-80mm.sale-pos-receipt[class][class][class] img.qr-code,
body.print-80mm.sale-pos-receipt[class][class][class] img[class*="qr"] {
    image-rendering: pixelated !important;
    background: transparent !important;
    mix-blend-mode: multiply !important;
    filter: grayscale(1) contrast(5) brightness(0.75) !important;
}
body.print-80mm.sale-pos-receipt[class][class][class] .sale-payment-qr-label {
    font-weight: 700 !important;
}
html:has(body.print-80mm),
body.print-80mm,
body.print-80mm.sale-pos-receipt[class][class][class] {
    overflow: visible !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
}
html:has(body.print-80mm)::-webkit-scrollbar,
html:has(body.print-80mm)::-webkit-scrollbar-button,
body.print-80mm::-webkit-scrollbar,
body.print-80mm::-webkit-scrollbar-button,
body.print-80mm.sale-pos-receipt[class][class][class]::-webkit-scrollbar,
body.print-80mm.sale-pos-receipt[class][class][class]::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
}
/* Single 80mm hierarchy used by Sale, PO, supplier, transfer, gate pass,
   order request, load form, and return receipts. */
body.print-80mm[class][class][class][class],
body.print-80mm[class][class][class][class] .line,
body.print-80mm[class][class][class][class] .info-row,
body.print-80mm[class][class][class][class] .sum-row,
body.print-80mm[class][class][class][class] table tbody td,
body.print-80mm[class][class][class][class] .footer { font-weight: 500 !important; }
body.print-80mm[class][class][class][class] .company,
body.print-80mm[class][class][class][class] .company-name,
body.print-80mm[class][class][class][class] .sale-invoice-title,
body.print-80mm[class][class][class][class] .invoice-word,
body.print-80mm[class][class][class][class] .doc-title,
body.print-80mm[class][class][class][class] .document-title,
body.print-80mm[class][class][class][class] .section-title,
body.print-80mm[class][class][class][class] .info-label,
body.print-80mm[class][class][class][class] table thead th { font-weight: 700 !important; }
body.print-80mm[class][class][class][class] .net-total-row,
body.print-80mm[class][class][class][class] .net-total-row *,
body.print-80mm[class][class][class][class] .tax-details-row,
body.print-80mm[class][class][class][class] .tax-details-row * { font-weight: 700 !important; }
body.print-80mm[class][class][class][class] .product-name-row td,
body.print-80mm[class][class][class][class] td.item-name,
body.print-80mm[class][class][class][class] td.col-item,
body.print-80mm[class][class][class][class] table tbody td { font-weight: 400 !important; }
body.print-80mm[class][class][class][class] .product-name-row td,
body.print-80mm[class][class][class][class] td.item-name,
body.print-80mm[class][class][class][class] td.col-item { font-weight: 700 !important; }
/* Final compact 80mm Sale profile. */
body.print-80mm.sale-pos-receipt[class][class][class][class] .print-header,
body.print-80mm.sale-pos-receipt[class][class][class][class] .subtitle,
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-invoice-title { text-align: center !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .company,
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-invoice-title,
body.print-80mm.sale-pos-receipt[class][class][class][class] .section-title,
body.print-80mm.sale-pos-receipt[class][class][class][class] table thead th { font-weight: 700 !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .line,
body.print-80mm.sale-pos-receipt[class][class][class][class] .sum-row { padding-top: 0 !important; padding-bottom: 0 !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] table tbody td { font-weight: 400 !important; padding-top: 1px !important; padding-bottom: 1px !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] table tbody .product-name-row td,
body.print-80mm.sale-pos-receipt[class][class][class][class] table tbody td.item-name { font-weight: 700 !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .net-total-row,
body.print-80mm.sale-pos-receipt[class][class][class][class] .net-total-row *,
body.print-80mm.sale-pos-receipt[class][class][class][class] .total-line,
body.print-80mm.sale-pos-receipt[class][class][class][class] .total-line *,
body.print-80mm.sale-pos-receipt[class][class][class][class] .tax-details-row,
body.print-80mm.sale-pos-receipt[class][class][class][class] .tax-details-row * { font-weight: 700 !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table { table-layout: fixed !important; width: 100% !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table td:nth-child(1) { width: 18% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table td:nth-child(2) { width: 12% !important; text-align: center !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table td:nth-child(3) { width: 25% !important; text-align: center !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table td:nth-child(4) { width: 20% !important; text-align: right !important; }
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt[class][class][class][class] .sale-items-table td:nth-child(5) { width: 25% !important; text-align: right !important; }
/* ── 3-column items table (tax & discount hidden): Rate | Qty | Total ── */
body.print-80mm[class][class][class] table.items-3col th:nth-child(1),
body.print-80mm[class][class][class] table.items-3col td:nth-child(1) { width: 28% !important; text-align: right !important; }
body.print-80mm[class][class][class] table.items-3col th:nth-child(2),
body.print-80mm[class][class][class] table.items-3col td:nth-child(2) { width: 18% !important; text-align: center !important; }
body.print-80mm[class][class][class] table.items-3col th:nth-child(3),
body.print-80mm[class][class][class] table.items-3col td:nth-child(3) { width: 54% !important; text-align: right !important; }
/* ── 3-column new-items-table (tax & discount hidden): Qty | Rate | Total ── */
body.print-80mm[class][class][class] table.new-items-table.items-3col th:nth-child(1),
body.print-80mm[class][class][class] table.new-items-table.items-3col td:nth-child(1) { width: 18% !important; text-align: center !important; }
body.print-80mm[class][class][class] table.new-items-table.items-3col th:nth-child(2),
body.print-80mm[class][class][class] table.new-items-table.items-3col td:nth-child(2) { width: 28% !important; text-align: right !important; }
body.print-80mm[class][class][class] table.new-items-table.items-3col th:nth-child(3),
body.print-80mm[class][class][class] table.new-items-table.items-3col td:nth-child(3) { width: 54% !important; text-align: right !important; }

/* Final semantic-column alignment for every 80mm item table.  The older
   nth-child rules assume Tax and Discount are present; when either column is
   hidden, those rules leave the visible values using the wrong column edges. */
body.print-80mm[class][class][class][class][class] table.items-table th.col-rate,
body.print-80mm[class][class][class][class][class] table.items-table td.col-rate { width: 18% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.items-table th.col-qty,
body.print-80mm[class][class][class][class][class] table.items-table td.col-qty { width: 12% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.items-table th.col-tax,
body.print-80mm[class][class][class][class][class] table.items-table td.col-tax { width: 25% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.items-table th.col-discount,
body.print-80mm[class][class][class][class][class] table.items-table td.col-discount { width: 20% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.items-table th.col-total,
body.print-80mm[class][class][class][class][class] table.items-table td.col-total { width: 25% !important; text-align: right !important; }

/* Returned items render Rate | Qty | Total when Tax and Discount are hidden. */
body.print-80mm[class][class][class][class][class] table.items-table.items-3col th.col-rate,
body.print-80mm[class][class][class][class][class] table.items-table.items-3col td.col-rate { width: 28% !important; }
body.print-80mm[class][class][class][class][class] table.items-table.items-3col th.col-qty,
body.print-80mm[class][class][class][class][class] table.items-table.items-3col td.col-qty { width: 18% !important; }
body.print-80mm[class][class][class][class][class] table.items-table.items-3col th.col-total,
body.print-80mm[class][class][class][class][class] table.items-table.items-3col td.col-total { width: 54% !important; }

/* Exchange items render Qty | Rate | Total when Tax and Discount are hidden. */
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col th.col-qty,
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col td.col-qty { width: 16% !important; }
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col th.col-rate,
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col td.col-rate { width: 28% !important; }
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col th.col-total,
body.print-80mm[class][class][class][class][class] table.new-items-table.items-3col td.col-total { width: 54% !important; }

/* POS Sale tables have No. and Item columns before the numeric columns. */
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-no,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-no { width: 5% !important; text-align: left !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-item,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-item { width: 31% !important; text-align: left !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-qty,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-qty { width: 8% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-rate,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-rate { width: 13% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-tax,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-tax { width: 18% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-discount,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-discount { width: 13% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table th.col-total,
body.print-80mm[class][class][class][class][class] table.sale-items-table td.col-total { width: 13% !important; text-align: right !important; }

/* PO/refund thermal tables start with Rate (or Qty for exchange); they do
   not have the Sale table's No./Item columns. */
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-rate,
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-rate,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-rate,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-rate { width: 28% !important; text-align: right !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-qty,
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-qty,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-qty,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-qty { width: 18% !important; text-align: center !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-total,
body.print-80mm.purchase-order-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-total,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col th.col-total,
body.print-80mm.refund-document[class][class][class][class][class] table.sale-items-table.items-3col td.col-total { width: 54% !important; text-align: right !important; }

body.print-80mm[class][class][class][class][class] table.items-table th,
body.print-80mm[class][class][class][class][class] table.items-table td { padding-left: 2px !important; padding-right: 2px !important; }

/* Receiving vouchers use a compact two-line product-name cell on 80mm paper. */
body.print-80mm.receiving-document[class][class][class][class][class] table td.col-item,
body.print-80mm.receiving-document[class][class][class][class][class] table td.item-name {
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    overflow: hidden !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    vertical-align: top !important;
}

/* A4/A5 receipts keep Total Items as a normal summary row, matching the
   lighter weight used by the other summary values. */
body.sale-document:not(.print-80mm) .total-items,
body.sale-document:not(.print-80mm) .total-items-row,
body.sale-document:not(.print-80mm) .total-items span,
body.sale-document:not(.print-80mm) .total-items-row span { font-weight: 500 !important; }
</style>`;

    const markedHtml = isThermal
        ? String(html).replace(/<body(\s[^>]*)?>/i, (tag, attributes = '') => {
              if (/\bclass\s*=\s*["'][^"']*["']/i.test(attributes)) {
                  return tag.replace(/class\s*=\s*(['"])([^'"]*)\1/i, (match, quote, classes) =>
                      classes.split(/\s+/).includes('print-80mm') ? tag : `class=${quote}${classes} print-80mm${quote}`,
                  );
              }
              return tag.replace(/>$/, ' class="print-80mm">');
          })
        : String(html);

    return markedHtml.includes('</head>') ? markedHtml.replace('</head>', `${styles}</head>`) : `${styles}${markedHtml}`;
}

/* ------------------------------------------------------------------------- *
 * Receipt preferences — the operator's header, text-size and table choices.
 *
 * Five configuration rows, all edited on the Receipts tab of Configurations:
 *
 *   receipt_logo_enabled         '1' | '0'                 default '1'
 *   company_headline             the slogan, free text     default ''
 *   receipt_header_alignment     left | center | right     default left
 *   receipt_paper_font_size      small | medium | large    default small
 *   receipt_paper_table_borders  '1' | '0'                 default '0'
 *
 * Four of them are applied here as the last layer in the print pipeline
 * (printAgent.printHtmlReceiptViaAgent), not inside each document template:
 * one place then covers every document type, the Print Agent and the browser
 * fallback alike. The slogan is the exception — it is markup, so each builder
 * drops receiptSloganHtml() under its company name and this layer only styles
 * it. Defaults are a strict no-op: with untouched settings a document leaves
 * here byte-for-byte as it arrived.
 *
 * The logo has one more gate than its switch: the application's own
 * placeholder logo never prints, whatever the switch says. It is what an
 * installation has until it uploads one (and what it goes back to when the
 * upload is removed), and a receipt carrying somebody else's artwork is worse
 * than a receipt with none. `company_logo` decides that, read from the same
 * configuration rows.
 * ------------------------------------------------------------------------- */

/**
 * Whether a company_logo value is the application's placeholder rather than
 * an upload. The row holds the asset URL the server seeded (see
 * ConfigurationController::index and the seeder), so the file name is the one
 * thing every deployment's URL has in common. An empty value is *not* the
 * placeholder — it is no logo at all, and the builders print nothing for it.
 */
export function isDefaultCompanyLogo(logo) {
    return /(?:^|\/)default-logo\.png(?:[?#]|$)/i.test(String(logo ?? '').trim());
}

export const RECEIPT_HEADER_ALIGNMENTS = ['left', 'center', 'right'];

/**
 * How much every px font size on A4 and A5 grows. `small` is the scale the
 * documents were designed at. The 80mm till roll is never scaled: its column
 * widths are tuned to the character count that fits across the paper.
 */
export const RECEIPT_PAPER_FONT_SCALES = { small: 1, medium: 1.2, large: 1.4 };

const PAPER_SIZES = ['A4', 'A5', 'A5P'];

// Every receipt builder stamps one of these on <body>. Reports and the
// calculator estimate carry none, and keep their own layout and scale.
const RECEIPT_BODY_CLASS =
    /<body\b[^>]*\bclass\s*=\s*["'][^"']*\b(?:sale-document|sale-pos-receipt|supplier-document|supplier-return-document|purchase-order-document|order-request-document|transfer-document|load-form-document|gatepass-document|refund-document|exchange-document|payment-document|receiving-document)\b/i;

// The specificity idiom the print stylesheets already use to settle a tie.
// This layer is injected last and has to beat every per-document header rule
// before it, the strongest of which stack five [class] selectors.
const HEADER_RULE = 'body[class][class][class][class][class][class]';

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function configFlagOn(value, fallback) {
    if (value === undefined || value === null || String(value).trim() === '') return fallback;
    return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
}

/** The slogan as typed, or '' — the row predates its use and may be unset. */
export function receiptSlogan(configurations = {}) {
    return String(configurations?.company_headline ?? '').trim();
}

/**
 * The slogan line a builder places directly under its company name, already
 * escaped. Empty when there is no slogan, so the template stays clean.
 */
export function receiptSloganHtml(configurations = {}) {
    const slogan = receiptSlogan(configurations);
    return slogan ? `<div class="company-slogan">${escapeHtml(slogan)}</div>` : '';
}

/**
 * The five rows read as what they mean, an unrecognised value falling back to
 * its default rather than to nothing.
 */
export function resolveReceiptPreferences(configurations = {}) {
    const config = configurations ?? {};
    const alignment = String(config.receipt_header_alignment ?? '')
        .trim()
        .toLowerCase();
    const fontSize = String(config.receipt_paper_font_size ?? '')
        .trim()
        .toLowerCase();

    return {
        showLogo: configFlagOn(config.receipt_logo_enabled, true) && !isDefaultCompanyLogo(config.company_logo),
        slogan: receiptSlogan(config),
        headerAlignment: RECEIPT_HEADER_ALIGNMENTS.includes(alignment) ? alignment : 'left',
        fontSize: Object.prototype.hasOwnProperty.call(RECEIPT_PAPER_FONT_SCALES, fontSize) ? fontSize : 'small',
        tableBorders: configFlagOn(config.receipt_paper_table_borders, false),
    };
}

function sloganStyles(size) {
    // A step below the address line and regular weight, so the company name
    // stays the loudest thing in the header on every paper.
    const px = size === 'A4' ? 11 : PAPER_SIZES.includes(size) ? 9.5 : 9;
    return `${HEADER_RULE} .company-slogan { display: block !important; font-size: ${px}px !important; line-height: 1.2 !important; font-weight: 400 !important; letter-spacing: 0 !important; text-transform: none !important; color: inherit !important; margin: 1px 0 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; text-align: inherit !important; }`;
}

/**
 * Where the header sits on A4 and A5. `left` is the layout the documents were
 * designed with — the name block on the left, the address block on the right —
 * and emits nothing. `right` mirrors it. `center` collapses the two blocks into
 * one centred column with the name block on top. The 80mm roll is a single
 * centred column already and is left alone.
 */
function headerAlignmentStyles(alignment) {
    const P = HEADER_RULE;
    if (alignment === 'right') {
        return [
            `${P} .header-row, ${P} .brand-row { flex-direction: row-reverse !important; }`,
            `${P} .header-left, ${P} .brand-left { display: flex !important; flex-direction: row-reverse !important; justify-content: flex-start !important; text-align: right !important; }`,
            `${P} .header-left .brand-details, ${P} .brand-left .brand-details { text-align: right !important; }`,
            `${P} .header-left .company-name, ${P} .header-left .company-slogan, ${P} .header-left .invoice-word, ${P} .header-left .invoice-no, ${P} .header-left .company-sub, ${P} .header-left .company-meta { text-align: right !important; }`,
            `${P} .header-right, ${P} .brand-right { text-align: left !important; }`,
            `${P} .header-right .company-sub, ${P} .header-right .company-meta, ${P} .header-right .header-address, ${P} .header-right .header-tax, ${P} .header-right .branch-detail { text-align: left !important; }`,
        ].join('\n');
    }
    if (alignment === 'center') {
        return [
            `${P} .header-row, ${P} .brand-row { flex-direction: column !important; align-items: center !important; justify-content: flex-start !important; gap: 1mm !important; }`,
            `${P} .header-left, ${P} .brand-left { display: flex !important; flex-direction: column !important; align-items: center !important; justify-content: flex-start !important; flex: none !important; width: 100% !important; max-width: 100% !important; gap: 1mm !important; text-align: center !important; }`,
            `${P} .header-right, ${P} .brand-right { display: block !important; flex: none !important; width: 100% !important; max-width: 100% !important; padding-top: 0 !important; text-align: center !important; }`,
            `${P} .header-left .brand-details, ${P} .brand-left .brand-details { width: 100% !important; text-align: center !important; }`,
            `${P} .header-left .logo, ${P} .header-left .logo-img, ${P} .brand-left .logo, ${P} .brand-left .logo-img { margin: 0 auto !important; }`,
            `${P} .header-row .company-name, ${P} .header-row .company-slogan, ${P} .header-row .invoice-word, ${P} .header-row .invoice-no, ${P} .header-row .company-sub, ${P} .header-row .company-meta, ${P} .header-row .header-address, ${P} .header-row .header-tax, ${P} .header-row .branch-detail { text-align: center !important; }`,
        ].join('\n');
    }
    return '';
}

/**
 * The line-item grid on A4 and A5. The documents were designed with horizontal
 * rules only — a line under the header row and under each item — and the A4
 * and A5 print layers strip every vertical cell border to keep it that way, so
 * `off` emits nothing. `on` boxes every cell of every table on the page: the
 * line items, and the receiving and payment logs that sit under them on a
 * purchase order, because a grid on one table and rules on the next reads as
 * a mistake. Totals are not tables on any paper document, so they are never
 * boxed. The 80mm roll has no room for a grid and is left alone.
 */
function tableBorderStyles() {
    const P = HEADER_RULE;
    return [`${P} table { border-collapse: collapse !important; }`, `${P} table th, ${P} table td { border: 1px solid #000 !important; }`].join('\n');
}

function scalePx(value, scale) {
    return String(Math.round(Number(value) * scale * 10) / 10);
}

/**
 * Apply the operator's receipt preferences to a finished document.
 *
 * Runs after every other stylesheet in the print chain, so it wins on document
 * order as well as on specificity. `receiptSize` decides what applies: the
 * logo switch and the slogan style on every paper, the header alignment, the
 * table borders and the text size on A4/A5 only.
 */
export function applyReceiptPreferences(html, receiptSize = '80mm', configurations = {}) {
    let output = String(html ?? '');
    if (!RECEIPT_BODY_CLASS.test(output)) return output;

    const preferences = resolveReceiptPreferences(configurations);
    const size = String(receiptSize || '80mm')
        .trim()
        .toUpperCase();
    const isPaper = PAPER_SIZES.includes(size);

    if (!preferences.showLogo) {
        // Removed rather than hidden: the Agent gets a smaller payload, and the
        // flex gap that sat beside the logo goes with it.
        output = output.replace(/<img\b[^>]*>/gi, (tag) => (/\bclass\s*=\s*["'][^"']*\blogo\b/i.test(tag) ? '' : tag));
    }

    const rules = [
        output.includes('class="company-slogan"') ? sloganStyles(size) : '',
        isPaper ? headerAlignmentStyles(preferences.headerAlignment) : '',
        isPaper && preferences.tableBorders ? tableBorderStyles() : '',
    ]
        .filter(Boolean)
        .join('\n');
    if (rules) {
        const styles = `<style data-inventree-receipt-preferences>\n${rules}\n</style>`;
        output = output.includes('</head>') ? output.replace('</head>', `${styles}</head>`) : `${styles}${output}`;
    }

    const scale = isPaper ? RECEIPT_PAPER_FONT_SCALES[preferences.fontSize] : 1;
    if (scale !== 1) {
        // Every px font size in the document — its own <style>, each injected
        // layer and inline style attributes — so the whole page grows together.
        // Widths, margins and line-heights are mm, % or unitless and follow.
        output = output.replace(
            /font-size(\s*:\s*)([^;}"']+)/gi,
            (match, colon, value) => `font-size${colon}${value.replace(/(\d*\.?\d+)px/gi, (px, number) => `${scalePx(number, scale)}px`)}`,
        );
    }

    return output;
}
